import { supabase } from '@/integrations/supabase/client';
import type {
  UserOrganization,
  UserRole,
  UserRow,
  UserManagementUser,
  UserInvitation,
  AppRole,
} from '../types/organization.types';
import { organizationMappers } from '../services/organization.mappers';

export const teamRepository = {
  /**
   * Fetches all organizations for the authenticated user.
   */
  async getUserOrganizations(): Promise<UserOrganization[]> {
    const { data, error } = await supabase
      .from('user_organizations')
      .select(`
        *,
        organization:organizations(*)
      `)
      .eq('is_active', true)
      .order('joined_at', { ascending: false });

    if (error) {
      console.error('Error fetching user organizations:', error);
      throw error;
    }

    return data as UserOrganization[];
  },

  /**
   * Fetches the role of the user in a specific organization.
   */
  async getCurrentUserRole(orgId: string): Promise<UserRole | null> {
    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (authError || !authData.user) {
      if (authError) console.error('Error fetching auth user for role:', authError);
      return null;
    }

    const { data, error } = await supabase
      .from('user_organizations')
      .select('*')
      .eq('user_id', authData.user.id)
      .eq('organization_id', orgId)
      .eq('is_active', true)
      .maybeSingle();

    if (error) {
      console.error('Error fetching user role:', error);
      return null;
    }

    return data as UserRole | null;
  },

  /**
   * Fetches all team members for an organization, joining user_organizations and users.
   */
  async getTeamMembers(orgId: string): Promise<UserRow[]> {
    const { data: userOrgs, error: userOrgsError } = await supabase
      .from('user_organizations')
      .select('id, user_id, role, is_active')
      .eq('organization_id', orgId);

    if (userOrgsError) {
      console.error('Error fetching team memberships:', userOrgsError);
      throw userOrgsError;
    }

    if (!userOrgs || userOrgs.length === 0) {
      return [];
    }

    const userIds = userOrgs.map((uo) => uo.user_id);
    const { data: users, error: usersError } = await supabase
      .from('users')
      .select('id, name, email, status')
      .in('id', userIds);

    if (usersError) {
      console.error('Error fetching user profiles for team:', usersError);
      throw usersError;
    }

    if (!users || users.length === 0) {
      return [];
    }

    const userMap = new Map(users.map((u) => [u.id, u]));

    return userOrgs
      .map((uo): UserRow | null => {
        const user = userMap.get(uo.user_id);
        if (!user) return null;
        return {
          id: user.id,
          name: user.name || 'Unknown User',
          email: user.email,
          status: user.status || 'Active',
          role: uo.is_active ? uo.role : null,
          user_org_id: uo.id,
        };
      })
      .filter((row): row is UserRow => row !== null);
  },

  /**
   * Fetches members using the org_user_view.
   */
  async getOrgUserView(orgId: string): Promise<UserManagementUser[]> {
    const { data: orgUsers, error } = await supabase
      .from('org_user_view')
      .select('*')
      .eq('organization_id', orgId)
      .eq('is_active', true)
      .order('joined_at', { ascending: false });

    if (error) {
      console.error('Error fetching org users:', error);
      throw error;
    }

    return (orgUsers || []).map(organizationMappers.mapOrgUserViewRow);
  },

  /**
   * Updates a member's role via user_organizations id.
   */
  async updateMemberRole(userOrgId: string, role: AppRole): Promise<void> {
    const { error } = await supabase
      .from('user_organizations')
      .update({ role })
      .eq('id', userOrgId);

    if (error) {
      console.error('Error updating member role:', error);
      throw error;
    }
  },

  /**
   * Updates a user's role via userId and orgId.
   */
  async updateUserRoleInOrg(orgId: string, userId: string, newRole: AppRole): Promise<void> {
    const { error } = await supabase
      .from('user_organizations')
      .update({ role: newRole })
      .eq('user_id', userId)
      .eq('organization_id', orgId);

    if (error) {
      console.error('Error updating role in org:', error);
      throw error;
    }

    const { data: currentUser } = await supabase.auth.getUser();
    if (currentUser.user) {
      await supabase.from('user_management_logs').insert({
        organization_id: orgId,
        admin_user_id: currentUser.user.id,
        target_user_id: userId,
        action: 'change_role',
        details: { new_role: newRole },
      });
    }
  },

  /**
   * Toggles a user's active/suspended status in an org.
   */
  async toggleUserStatus(orgId: string, userId: string, newStatus: 'active' | 'suspended'): Promise<void> {
    const { error } = await supabase
      .from('user_organizations')
      .update({ is_active: newStatus === 'active' })
      .eq('user_id', userId)
      .eq('organization_id', orgId);

    if (error) {
      console.error('Error toggling user status:', error);
      throw error;
    }

    const { data: currentUser } = await supabase.auth.getUser();
    if (currentUser.user) {
      await supabase.from('user_management_logs').insert({
        organization_id: orgId,
        admin_user_id: currentUser.user.id,
        target_user_id: userId,
        action: newStatus === 'active' ? 'reactivate_user' : 'deactivate_user',
      });
    }
  },

  /**
   * Adds a team member directly or invites them.
   */
  async addTeamMember(orgId: string, name: string, email: string, role: AppRole): Promise<void> {
    const { data: existingUser } = await supabase
      .from('users')
      .select('id')
      .eq('email', email)
      .maybeSingle();

    let userId = existingUser?.id;

    if (!userId) {
      const tempAuthId = crypto.randomUUID();
      const { data: newUser, error: createErr } = await supabase
        .from('users')
        .insert([{ email, name, status: 'active', auth_user_id: tempAuthId }])
        .select()
        .single();

      if (createErr) throw createErr;
      userId = newUser?.id;
      if (!userId) throw new Error('User created but id missing');
    }

    const { error: userOrgErr } = await supabase
      .from('user_organizations')
      .insert([
        {
          user_id: userId,
          organization_id: orgId,
          role,
          is_active: true,
        },
      ]);

    if (userOrgErr) throw userOrgErr;
  },

  /**
   * Removes or deactivates a member from the organization.
   */
  async removeMember(
    param: string | { userId?: string; userOrgId?: string; action?: 'deactivate' | 'delete' },
    actionFallback: 'deactivate' | 'delete' = 'deactivate'
  ): Promise<void> {
    const userOrgId = typeof param === 'string' ? param : param.userOrgId;
    const userId = typeof param === 'string' ? undefined : param.userId;
    const action = typeof param === 'string' ? actionFallback : (param.action || actionFallback);

    if (action === 'deactivate') {
      if (userOrgId) {
        const { error } = await supabase
          .from('user_organizations')
          .update({ is_active: false })
          .eq('id', userOrgId);

        if (error) throw error;
      }
    } else {
      if (userOrgId) {
        const { error } = await supabase
          .from('user_organizations')
          .delete()
          .eq('id', userOrgId);

        if (error) throw error;
      }
      if (userId) {
        const { error } = await supabase
          .from('users')
          .delete()
          .eq('id', userId);

        if (error) throw error;
      }
    }
  },

  /**
   * Fetches invitations for the current organization.
   */
  async getInvitations(): Promise<UserInvitation[]> {
    const { data, error } = await supabase
      .from('user_invitations')
      .select('*')
      .order('invited_at', { ascending: false });

    if (error) {
      console.error('Error fetching user invitations:', error);
      throw error;
    }

    return (data || []).map(organizationMappers.mapInvitationRow);
  },

  /**
   * Creates an invitation by generating token, inserting record, and dispatching invitation email.
   */
  async inviteUser(orgId: string, email: string, role: AppRole): Promise<UserInvitation> {
    const { data: tokenData, error: tokenError } = await supabase.rpc('generate_invite_token');
    if (tokenError) {
      console.error('Error generating token:', tokenError);
      throw tokenError;
    }

    const { data: currentUser, error: userError } = await supabase.auth.getUser();
    if (userError || !currentUser.user) {
      throw new Error('Not authenticated');
    }

    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);

    const { data, error } = await supabase
      .from('user_invitations')
      .insert({
        organization_id: orgId,
        email,
        role,
        invited_by: currentUser.user.id,
        invite_token: tokenData,
        expires_at: expiresAt.toISOString(),
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating invitation:', error);
      throw error;
    }

    try {
      // Send invitation email via Edge Function
      const { data: emailResponse, error: emailError } = await supabase.functions.invoke('send-user-invitation', {
        body: {
          email,
          role,
          inviteToken: tokenData,
          organizationId: orgId,
        },
      });

      if (emailError) {
        console.error('Error calling send-user-invitation function:', emailError);
        throw emailError;
      }

      if (emailResponse && !emailResponse.success) {
        console.error('send-user-invitation returned unsuccessful response:', emailResponse);
        throw new Error(emailResponse.error || 'Failed to send invitation email');
      }

      // Log the action to audit log
      await supabase.from('user_management_logs').insert({
        organization_id: orgId,
        admin_user_id: currentUser.user.id,
        target_email: email,
        action: 'invite_user',
        details: {
          email,
          role,
          invitation_id: data.id,
          email_id: emailResponse?.emailId,
        },
      });
    } catch (sendErr) {
      console.error('Failed to send invitation email:', sendErr);
      // Clean up the created invitation row to avoid leaving an un-emailed pending record
      await supabase.from('user_invitations').delete().eq('id', data.id);
      throw sendErr;
    }

    return organizationMappers.mapInvitationRow(data);
  },

  /**
   * Resends an invitation email.
   */
  async resendInvitation(invitationId: string): Promise<UserInvitation> {
    const { data: invitation, error: fetchError } = await supabase
      .from('user_invitations')
      .select('*')
      .eq('id', invitationId)
      .single();

    if (fetchError) throw fetchError;
    if (invitation.status !== 'pending') {
      throw new Error('Can only resend pending invitations');
    }

    const { data: emailResponse, error: emailError } = await supabase.functions.invoke('resend-user-invitation', {
      body: { invitationId },
    });

    if (emailError) {
      console.error('Error calling resend-user-invitation function:', emailError);
      throw emailError;
    }

    if (emailResponse && !emailResponse.success) {
      console.error('resend-user-invitation returned unsuccessful response:', emailResponse);
      throw new Error(emailResponse.error || 'Failed to resend invitation email');
    }

    const { data: currentUser } = await supabase.auth.getUser();
    if (currentUser?.user) {
      await supabase.from('user_management_logs').insert({
        organization_id: invitation.organization_id,
        admin_user_id: currentUser.user.id,
        target_email: invitation.email,
        action: 'resend_invitation',
        details: { invitation_id: invitationId },
      });
    }

    return organizationMappers.mapInvitationRow(invitation);
  },

  /**
   * Cancels a pending invitation.
   */
  async cancelInvitation(invitationId: string): Promise<void> {
    const { error } = await supabase
      .from('user_invitations')
      .delete()
      .eq('id', invitationId);

    if (error) {
      console.error('Error cancelling invitation:', error);
      throw error;
    }
  },
};
